/**
 * Public payment API service.
 *
 * Deliberately separate from api.ts — no auth header, no credentials,
 * no token refresh. These endpoints are openly accessible by paying clients.
 */

import type { PublicInvoiceView } from '@aelita/shared';

// Same-origin: requests go to /api/... and are proxied by Vite dev server / nginx in prod.
// No import.meta.env needed — mirrors the approach in api.ts.
const PUBLIC_API_BASE = '';

// ---------------------------------------------------------------------------
// Result type — lets callers pattern-match instead of try/catch
// ---------------------------------------------------------------------------

export type PublicApiOk<T> = { ok: true; data: T };
export type PublicApiErr = { ok: false; status: number; reason?: string };
export type PublicApiResult<T> = PublicApiOk<T> | PublicApiErr;

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

export type InvoiceErrorReason = 'not_found' | 'expired' | 'used' | 'invoice_not_payable';

/**
 * Shape of a successful API envelope: { success: true, data: T }
 * Used internally by publicFetch to unwrap the inner payload.
 */
interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { code?: string; reason?: string; message?: string };
  reason?: string; // legacy flat reason (not used by this API, but guarded)
}

export interface CheckoutResult {
  checkoutUrl: string;
}

/**
 * Payment session result — subset returned by GET /api/public/pay/success.
 * amount is a decimal string (e.g. "250.00") as serialised by Prisma Decimal.
 * status mirrors PaymentStatus enum: 'pending' | 'succeeded' | 'failed' | 'refunded'.
 */
export interface PaymentSessionResult {
  status: 'pending' | 'succeeded' | 'failed' | 'refunded';
  invoiceNumber: string;
  amount: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function publicFetch<T>(path: string, init?: RequestInit): Promise<PublicApiResult<T>> {
  try {
    const res = await fetch(`${PUBLIC_API_BASE}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    });

    // Try to parse JSON regardless of status — error bodies carry `reason`
    let body: ApiEnvelope<T> = { success: false };
    try {
      body = (await res.json()) as ApiEnvelope<T>;
    } catch {
      // Non-JSON body (e.g. nginx 502 HTML) — ignore
    }

    if (!res.ok) {
      // Error shape: { success: false, error: { code, reason } }
      // Reason may be nested under `error` (primary path) or flat on body (fallback).
      const reason =
        typeof body.error?.reason === 'string'
          ? body.error.reason
          : typeof body.reason === 'string'
            ? body.reason
            : undefined;
      return { ok: false, status: res.status, reason };
    }

    // Success shape: { success: true, data: T }
    // Guard: if body.data is absent despite success:true, surface as error so
    // the page renders an error state instead of crashing on undefined fields.
    if (body.data === undefined || body.data === null) {
      return { ok: false, status: res.status };
    }

    return { ok: true, data: body.data };
  } catch {
    // Network-level failure
    return { ok: false, status: 0 };
  }
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function getPublicInvoice(token: string): Promise<PublicApiResult<PublicInvoiceView>> {
  return publicFetch<PublicInvoiceView>(`/api/public/pay/${token}`);
}

export async function createCheckoutSession(
  token: string,
): Promise<PublicApiResult<CheckoutResult>> {
  return publicFetch<CheckoutResult>(`/api/public/pay/${token}/checkout`, {
    method: 'POST',
  });
}

/**
 * Fetch payment status for a Stripe Checkout session.
 * Returns ok:false with status 404 when no Payment row exists yet (still processing).
 */
export async function getPaymentSession(
  sessionId: string,
): Promise<PublicApiResult<PaymentSessionResult>> {
  return publicFetch<PaymentSessionResult>(
    `/api/public/pay/success?session=${encodeURIComponent(sessionId)}`,
  );
}
